/* global axios */
import { isOrganizationAccount } from '../../helper/organizationAccessHelper';

const knowledgeProxy = (accountId, path) =>
  `/api/v1/accounts/${accountId}/proxy/knowledge${path}`;

// v1's ServiceResponse/StaffResponse are flat (service_id/service_name, a
// single branch_id per staff) since a legacy service/staff belongs to exactly
// one branch — unlike v2 where both can span branches. Normalize v1 results
// into the v2 ServiceV2Response shape ({ service, branches, staff }, staff
// carrying a `branches` array) so createAppointmentModal.vue can consume
// either without knowing which API version served it.
const normalizeV1Service = item => ({
  service: {
    id: item.service_id,
    name: item.service_name,
    description: item.description,
    duration: item.duration,
    price: item.price,
    max_serve: item.max_serve,
    image: item.image,
  },
  branches: item.branch_id ? [{ id: item.branch_id, name: item.branch?.name }] : [],
  staff: (item.staff || []).map(staff => ({
    id: staff.staff_id,
    name: staff.staff_name,
    is_active: true,
    branches: staff.branch_id ? [{ id: staff.branch_id, name: staff.branch?.name }] : [],
  })),
  stats: null,
});

const state = {
  services: [],
  allServices: [],
  isAllServicesError: false,
  isAllServicesLoading: false,
  selectedService: null,
  pagination: {
    page: 1,
    page_size: 10,
    total: 0,
    next: null,
  },
  filters: { 
    branch_id: null, 
    active: null, 
    enabled: null, 
    q: null,
    aoikumo_integration_id: null,
  },
  isLoading: false,
  isError: false,
  importStatus: null,
  importProgress: null,
  allBranchServices: [],
  isAllBranchServicesLoading: false,
  isAllBranchServicesError: false,
};

const getters = {
  getServices: state => state.services,
  getSelectedService: state => state.selectedService,
  getServicePagination: state => state.pagination,
  formattedPagination: (state) => {
    const { page, total, next, page_size } = state.pagination;
    return {
      currentPage: page,
      totalPages: Math.max(1, Math.ceil(total / page_size)),
      nextPage: next,
      pageSize: page_size,
    };
  },
};

const mutations = {
  SET_SERVICES(state, services) { state.services = services; },
  SET_SELECTED_SERVICE(state, service) { state.selectedService = service; },
  SET_PAGINATION(state, pagination) { state.pagination = pagination; },
  SET_FILTERS(state, filters) { state.filters = { ...state.filters, ...filters }; },
  SET_IS_LOADING(state, isLoading) { state.isLoading = isLoading; },
  SET_IS_ERROR(state, isError) { state.isError = isError; },
  SET_IMPORT_STATUS(state, status) { state.importStatus = status; },
  SET_IMPORT_PROGRESS(state, progress) { state.importProgress = progress; },
  SET_IS_ALL_SERVICES_LOADING(state, isLoading) { state.isAllServicesLoading = isLoading; },
  SET_IS_ALL_SERVICES_ERROR(state, isError) { state.isAllServicesError = isError; },
  SET_ALL_SERVICES(state, allServices) { state.allServices = allServices; },
  SET_ALL_BRANCH_SERVICES(state, services) { state.allBranchServices = services; },
  SET_IS_ALL_BRANCH_SERVICES_LOADING(state, isLoading) { state.isAllBranchServicesLoading = isLoading; },
  SET_IS_ALL_BRANCH_SERVICES_ERROR(state, isError) { state.isAllBranchServicesError = isError; },
};

const actions = {
  async fetchServices({ commit, rootGetters, state }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_LOADING', true);
      commit('SET_IS_ERROR', false);

      const { branch_id, active, enabled, q, aoikumo_integration_id } = state.filters;

      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services`),
        {
          params: {
            branch_id, active, enabled, q, aoikumo_integration_id,
            page: state.pagination.page,
            page_size: state.pagination.page_size,
          },
        }
      );

      const { results, page: currentPage, total, next } = res.data;
      commit('SET_SERVICES', results);
      commit('SET_PAGINATION', { ...state.pagination, page: currentPage, total, next });
      return res.data;
    } catch (error) {
      console.error('Fetch services failed:', error);
      commit('SET_IS_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_LOADING', false);
    }
  },

  async fetchServiceById({ commit, rootGetters }, { serviceId, branch_id = null }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}`),
        { params: { branch_id } }
      );
      commit('SET_SELECTED_SERVICE', res.data);
      return res.data;
    } catch (error) {
      console.error('Fetch service failed:', error);
      throw error.response?.data || error;
    }
  },

  async createService({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const formData = new FormData();
      for (const key in payload) {
        if (Array.isArray(payload[key])) {
          payload[key].forEach(value => formData.append(key, value));
        } else if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      }

      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (error) {
      console.error('Create service failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateService({ rootGetters }, { serviceId, payload }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const formData = new FormData();
      for (const key in payload) {
        if (key === 'delete_image_ids') {
          if (Array.isArray(payload[key])) {
            const idsString = payload[key].join(',');
            if (idsString) formData.append(key, idsString);
          } else if (payload[key] !== null && payload[key] !== undefined && payload[key] !== '') {
            formData.append(key, payload[key]);
          }
        } else if (Array.isArray(payload[key])) {
          payload[key].forEach(value => formData.append(key, value));
        } else if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      }

      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (error) {
      console.error('Update service failed:', error);
      throw error.response?.data || error;
    }
  },

  async deleteService({ rootGetters }, serviceId) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.delete(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}`)
      );
      return res.data;
    } catch (error) {
      console.error('Delete service failed:', error);
      throw error.response?.data || error;
    }
  },

  async toggleService({ rootGetters }, { serviceId, disabled_reason = '', isActive = null }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}/status`),
        { is_active: isActive, disabled_reason }
      );
      return res.data;
    } catch (error) {
      console.error('Toggle service failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateServiceStatus({ rootGetters }, { serviceId, is_active }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const res = await axios.patch(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/${serviceId}/status`),
        { is_active }
      );
      return res.data;
    } catch (error) {
      console.error('Update service status failed:', error);
      throw error.response?.data || error;
    }
  },

  async updatePaginationAndFetch({ commit, dispatch, state }, newPagination) {
    try {
      commit('SET_PAGINATION', { ...state.pagination, ...newPagination });
      await dispatch('fetchServices', state.filters);
      return true;
    } catch (error) {
      console.error('Update pagination and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async updateFiltersAndFetch({ commit, dispatch, state }, filters = {}) {
    try {
      commit('SET_FILTERS', filters);
      commit('SET_PAGINATION', { ...state.pagination, page: 1 });
      await dispatch('fetchServices', state.filters);
      return true;
    } catch (error) {
      console.error('Update filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async clearFiltersAndFetch({ commit, dispatch }) {
    try {
      commit('SET_PAGINATION', { page: 1, page_size: 10, total: 0, next: null });
      commit('SET_FILTERS', { branch_id: null, active: null, enabled: null, q: null, aoikumo_integration_id: null });
      await dispatch('fetchServices', state.filters);
      return true;
    } catch (error) {
      console.error('Clear filters and fetch failed:', error);
      throw error.response?.data || error;
    }
  },

  async importServices({ rootGetters }, payload) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      const formData = new FormData();
      for (const key in payload) {
        if (Array.isArray(payload[key])) {
          payload[key].forEach(value => formData.append(key, value));
        } else if (payload[key] !== null && payload[key] !== undefined) {
          formData.append(key, payload[key]);
        }
      }

      const res = await axios.post(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/import`),
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      return res.data;
    } catch (error) {
      console.error('Import service failed:', error);
      throw error.response?.data || error;
    }
  },

  async getImportStatus({ rootGetters, commit }, importId) {
    const accountId = rootGetters.getCurrentAccountId;
    const pollInterval = () => Math.floor(Math.random() * 3000) + 2000;
    const delay = ms => new Promise(resolve => setTimeout(resolve, ms));

    const fetchStatus = async () => {
      const res = await axios.get(
        knowledgeProxy(accountId, `/v2/accounts/${accountId}/services/import/${importId}`)
      );
      return res.data;
    };

    try {
      let response = await fetchStatus();

      commit('SET_IMPORT_STATUS', response.status);
      commit('SET_IMPORT_PROGRESS', response.progress?.percentage || 0);

      while (response.status === 'queued' || response.status === 'processing') {
        await delay(pollInterval());
        response = await fetchStatus();

        commit('SET_IMPORT_STATUS', response.status);
        commit('SET_IMPORT_PROGRESS', response.progress?.percentage || 0);
      }

      commit('SET_IMPORT_STATUS', response.status);
      commit('SET_IMPORT_PROGRESS', response.progress?.percentage || 100);

      return response;
    } catch (error) {
      console.error('Import status polling failed:', error);
      commit('SET_IMPORT_STATUS', 'failed');
      throw error.response?.data || error;
    }
  },

  async fetchAllServices({ commit, rootGetters }) {
    const accountId = rootGetters.getCurrentAccountId;
    try {
      commit('SET_IS_ALL_SERVICES_LOADING', true);
      commit('SET_IS_ALL_SERVICES_ERROR', false);
      const MAX_PAGE_SIZE = 100;
      let allServices = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await axios.get(
          knowledgeProxy(accountId, `/v2/accounts/${accountId}/services`),
          { params: { page: currentPage, page_size: MAX_PAGE_SIZE } }
        );

        const { results, total } = response.data;
        allServices = [...allServices, ...results];

        const totalPages = Math.ceil(total / MAX_PAGE_SIZE);
        hasMorePages = currentPage < totalPages;
        currentPage++;

        if (hasMorePages) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      commit('SET_ALL_SERVICES', allServices);
      return allServices;
    } catch (error) {
      console.error('Fetch all services sequential failed:', error);
      commit('SET_IS_ALL_SERVICES_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_ALL_SERVICES_LOADING', false);
    }
  },

  async fetchAllBranchServices({ commit, rootGetters }, branchId) {
    const accountId = rootGetters.getCurrentAccountId;
    const isOrganization = isOrganizationAccount({ getters: rootGetters }, accountId);
    const path = isOrganization
      ? `/v2/accounts/${accountId}/services`
      : `/accounts/${accountId}/services`;
    try {
      commit('SET_IS_ALL_BRANCH_SERVICES_LOADING', true);
      commit('SET_IS_ALL_BRANCH_SERVICES_ERROR', false);
      const MAX_PAGE_SIZE = 100;
      let allServices = [];
      let currentPage = 1;
      let hasMorePages = true;

      while (hasMorePages) {
        const response = await axios.get(
          knowledgeProxy(accountId, path),
          { params: { branch_id: branchId, page: currentPage, page_size: MAX_PAGE_SIZE } }
        );

        const { results, total } = response.data;
        allServices = [
          ...allServices,
          ...(isOrganization ? results : results.map(normalizeV1Service)),
        ];

        const totalPages = Math.ceil(total / MAX_PAGE_SIZE);
        hasMorePages = currentPage < totalPages;
        currentPage++;

        if (hasMorePages) {
          await new Promise(resolve => setTimeout(resolve, 100));
        }
      }

      commit('SET_ALL_BRANCH_SERVICES', allServices);
      return allServices;
    } catch (error) {
      console.error('Fetch all branch services sequential failed:', error);
      commit('SET_IS_ALL_BRANCH_SERVICES_ERROR', true);
      throw error.response?.data || error;
    } finally {
      commit('SET_IS_ALL_BRANCH_SERVICES_LOADING', false);
    }
  },

  resetPaginationAndFilters({ commit }) {
    commit('SET_PAGINATION', { page: 1, page_size: 10, total: 0, next: null });
    commit('SET_FILTERS', { branch_id: null, active: null, enabled: null, q: null, aoikumo_integration_id: null });
    return true;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
